import React, { useEffect, useMemo, useState } from 'react';
import { Table, Text, Box, TextInput, Drawer, Group, Card, Stack, useMantineTheme } from '@mantine/core';
import { router, usePage } from '@inertiajs/react';
import { StablePagination } from '@/components/StablePagination';
import { IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMediaQuery } from '@mantine/hooks';
import '../../styles/AdminStarStyles.css';

interface Purchase {
  id: number;
  stars: number;
  amount: number;
  stripe_transaction_id: string | null;
  created_at: string;
  message: string | null;
  supporter_name: string | null;
  user: { username: string; email: string } | null;
  artist: { id: number; name: string } | null;
}

interface PageProps {
  star_purchases: Purchase[];
  pagy: { page: number; items: number; pages: number; count: number };
  search: string;
}

const StarPurchasesIndex: React.FC = () => {
  const { star_purchases, pagy, search } = usePage<PageProps>().props;
  const [searchQuery, setSearchQuery] = useState(search || '');
  const [activeItem, setActiveItem] = useState<Purchase | null>(null);
  const [currentPage, setCurrentPage] = useState(pagy.page);
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  // Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get(
        route('admin_star_purchases_index'),
        { search: searchQuery, page: 1 },
        { preserveScroll: true, preserveState: true },
      );
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    router.get(
      route('admin_star_purchases_index'),
      { search: searchQuery, page: newPage },
      { preserveScroll: true, preserveState: true },
    );
  };

  const renderRows = () =>
    star_purchases.map((purchase) => (
      <tr key={purchase.id} onClick={() => setActiveItem(purchase)} style={{ cursor: 'pointer' }}>
        <td>{purchase.user?.username || purchase.supporter_name || '—'}</td>
        <td>{purchase.artist?.name || '—'}</td>
        <td>{purchase.stars}</td>
        <td>${(purchase.stars * 5).toFixed(2)}</td>
        <td>${(purchase.stars * 5 * 0.05).toFixed(2)}</td>
        <td>${(purchase.stars * 5 * 1.05).toFixed(2)}</td>
        <td>{dayjs(purchase.created_at).format('MMM D, YYYY')}</td>
        <td>{purchase.message || '—'}</td>
      </tr>
    ));

  const renderCards = () =>
    star_purchases.map((purchase) => (
      <Card key={purchase.id} onClick={() => setActiveItem(purchase)} shadow="sm" mb="md" withBorder>
        <Stack gap="xs">
          <Text>
            <strong>Purchaser:</strong> {purchase.user?.username || purchase.supporter_name || '—'}
          </Text>
          <Text>
            <strong>Artist:</strong> {purchase.artist?.name || '—'}
          </Text>
          <Text>
            <strong>Stars:</strong> {purchase.stars} ⭐ (${(purchase.stars * 5).toFixed(2)})
          </Text>
          <Text>
            <strong>Platform Fee:</strong> ${(purchase.stars * 5 * 0.05).toFixed(2)}
          </Text>
          <Text>
            <strong>Total Charge:</strong> ${(purchase.stars * 5 * 1.05).toFixed(2)}
          </Text>
          <Text>
            <strong>Date:</strong> {dayjs(purchase.created_at).format('MMM D, YYYY')}
          </Text>
          <Text>
            <strong>Message:</strong> {purchase.message || '—'}
          </Text>
        </Stack>
      </Card>
    ));

  return (
    <Box p="xl" className="mt-3">
      <Group justify="space-between" align="center" mb="md">
        <Text size="xl" fw={700} className="rubik-header">
          Star Purchases Summary
        </Text>
        <TextInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          placeholder="Search by purchaser, email, artist..."
          leftSection={<IconSearch size={16} />}
          w={300}
        />
      </Group>

      {isMobile ? (
        renderCards()
      ) : (
        <Table
          striped
          highlightOnHover
          withTableBorder
          verticalSpacing="2rem"
          horizontalSpacing="lg"
          style={{ color: 'white' }}
          className="star-table"
        >
          <thead>
            <tr>
              <th>Purchaser</th>
              <th>Artist</th>
              <th>Stars</th>
              <th>Base Charge</th>
              <th>Platform Fee</th>
              <th>Total Charge</th>
              <th>Date</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </Table>
      )}

      <Box mt="md">
        <StablePagination total={pagy.pages} value={currentPage} onChange={handlePageChange} />
      </Box>

      <Drawer
        opened={!!activeItem}
        onClose={() => setActiveItem(null)}
        title="Star Purchase Details"
        position="right"
        size="sm"
        styles={{
          header: { backgroundColor: '#17171790', color: 'white', fontWeight: 700 },
          body: { backgroundColor: '#171717', color: 'white', minHeight: '100vh' },
        }}
      >
        {activeItem && (
          <Box className="pt-3">
            <Text fw={700} c="orange" size="xl">
              Name:
            </Text>
            <Text className="mb-3" size="lg">
              {activeItem.user?.username || activeItem.supporter_name || '—'}
            </Text>

            <Text fw={700} c="orange" size="xl">
              Stars:
            </Text>
            <Text className="mb-3" size="lg">
              {activeItem.stars} ⭐ (${(activeItem.stars * 5).toFixed(2)})
            </Text>

            <Text fw={700} c="orange" size="xl">
              Message:
            </Text>
            <Text className="mb-3" size="lg">
              {activeItem.message || '—'}
            </Text>

            <Text fw={700} c="orange" size="xl">
              Amount Paid (with fees):
            </Text>
            <Text className="mb-3" size="lg">
              ${(activeItem.amount / 100).toFixed(2)}
            </Text>

            {activeItem.stripe_transaction_id && (
              <>
                <Text fw={700} c="orange" size="xl">
                  Stripe Transaction ID:
                </Text>
                <Text className="mb-3" size="lg">
                  {activeItem.stripe_transaction_id}
                </Text>
              </>
            )}

            <Text fw={700} c="orange" size="xl">
              Date:
            </Text>
            <Text className="mb-3" size="lg">
              {dayjs(activeItem.created_at).format('MMMM D, YYYY')}
            </Text>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default StarPurchasesIndex;
